/* global BigInt */
// Order-line discount view-model (pure, no money arithmetic): the sales row stores price = NET and the
// stamp (price_applicable, discount_pct, discount_amount). Read them as exact text; never round.
const txt = (v) => (v == null ? null : String(v));
const positive = (v) => v != null && /[1-9]/.test(String(v));

// -> { hasDisc, rows:[{ disc, price, pct, net }] }; `price` = applicable when discounted, else the net as sold.
export function orderLineDiscounts(lines) {
  const rows = (lines || []).map((l) => {
    const disc = positive(l.discount_pct) && l.price_applicable != null;
    return {
      disc,
      price: disc ? txt(l.price_applicable) : txt(l.net_price ?? l.unit_price),
      pct: disc ? txt(l.discount_pct) : null,
      net: txt(l.net_price ?? l.unit_price),
    };
  });
  return { hasDisc: rows.some((r) => r.disc), rows };
}

// True when a price text carries more meaningful decimals than the rate_decimals setting shows (3.333333 at 2):
// the table shows the setting's rounding, the exact text goes in a tooltip.
export function hasHiddenDecimals(v, shown = 2) {
  if (v == null) return false;
  const frac = (String(v).split('.')[1] || '').replace(/0+$/, '');
  return frac.length > shown;
}

// ── Line Edit panel: typed discount % (pure; mirrors lib/saleUnit.js deriveLineDiscountStamp) ──
const PCT_DECIMALS = 2;   // the API's discount_pct scale: more decimals is refused (422)
const dec12 = (t) => {   // '2000.5' -> 2000500000000n (12 dp); null when not a plain non-negative decimal with <= 12 dp
  const m = /^(\d+)(?:\.(\d{0,12}))?$/.exec(String(t).trim());
  return m ? BigInt(m[1]) * 10n ** 12n + BigInt((m[2] || '').padEnd(12, '0')) : null;
};

// Typed percent text -> { ok:true, text } (comma or dot accepted, normalised to a dot) | { ok:false, code }.
// code: required | invalid | range | decimals. 0 is valid (clears the discount).
export function parsePct(input, maxDecimals = PCT_DECIMALS) {
  const s = String(input ?? '').trim().replace(',', '.');
  if (s === '') return { ok: false, code: 'required' };
  const m = /^(\d+)(?:\.(\d*))?$/.exec(s);
  if (!m) return { ok: false, code: 'invalid' };
  if ((m[2] || '').replace(/0+$/, '').length > maxDecimals) return { ok: false, code: 'decimals' };
  if (Number(s) > 100) return { ok: false, code: 'range' };
  return { ok: true, text: s };
}

// net = applicable - round_half_up(applicable x pct / 100), exact (BigInt), as text; null when an input is unusable.
export function netAfterDiscount(applicable, pct) {
  const a = dec12(applicable), p = dec12(pct);
  if (a == null || p == null || p > 100n * 10n ** 12n) return null;
  const den = 100n * 10n ** 12n;
  const amount = (a * p * 2n + den) / (2n * den);
  const net = a - amount, f = String(net % 10n ** 12n).padStart(12, '0').replace(/0+$/, '');
  return `${net / 10n ** 12n}${f ? '.' + f : ''}`;
}

const SCALE = 10n ** 12n;
const txt12 = (n) => { const neg = n < 0n; const a = neg ? -n : n; const f = String(a % SCALE).padStart(12, '0').replace(/0+$/, ''); return `${neg ? '-' : ''}${a / SCALE}${f ? '.' + f : ''}`; };
// exact a x b as text (12 dp, half up), null when an input is not a plain decimal. No float: this is the one multiplication the Line Edit panel needs.
export function mulExact(a, b) {
  const x = dec12(a), y = dec12(b);
  return x == null || y == null ? null : txt12((x * y * 2n + SCALE) / (2n * SCALE));
}
// exact equality of two decimal texts (false when either is unusable)
export const sameAmount = (a, b) => { const x = dec12(a), y = dec12(b); return x != null && x === y; };

// Live preview of an edit: { net (text|null), delta (signed text|null) }. `currentNet` is the stored net per entry unit (text),
// `delivered` the delivered qty in entry units (text): the delivered slice posts (new net - old net) x delivered. Exact (BigInt), never a float.
export function previewLineEdit({ applicable, pct, currentNet, delivered }) {
  const net = netAfterDiscount(applicable, pct);
  const n = dec12(net), c = dec12(currentNet), q = dec12(delivered ?? '0');
  if (n == null || c == null || q == null) return { net: net ?? null, delta: null };
  const d = (n - c) * q, neg = d < 0n, ad = neg ? -d : d;
  return { net, delta: txt12((neg ? -1n : 1n) * ((ad * 2n + SCALE) / (2n * SCALE))) };
}

// Seed text for the % field from the stored stamp ('5.000000' -> '5'; no stamp -> '0').
export function seedPct(line) {
  const v = line?.discount_pct;
  if (v == null || !/^\d+(\.\d*)?$/.test(String(v))) return '0';
  const [i, f = ''] = String(v).split('.');
  const frac = f.replace(/0+$/, '');
  return `${i.replace(/^0+(?=\d)/, '')}${frac ? '.' + frac : ''}`;
}

// The body field: only a CHANGED valid % is sent, so an untouched Save keeps the stored % (and a legacy line unstamped).
export function pctPatch(typed, line) {
  const p = parsePct(typed);
  return p.ok && Number(p.text) !== Number(seedPct(line)) ? { discount_pct: p.text } : {};
}
